import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Play, RefreshCw, CheckCircle2, XCircle, Circle } from "lucide-react";
import { api } from "@/lib/api";
import { ErrorState } from "@/components/empty-state";

export const Route = createFileRoute("/admin/gamebillet")({
  component: AdminGameBillet,
});

function AdminGameBillet() {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ["admin-gamebillet-status"],
    queryFn: () => api.adminGameBilletStatus(),
    refetchInterval: 4000,
  });
  const errors = useQuery({
    queryKey: ["admin-gamebillet-errors"],
    queryFn: () => api.adminImportErrors(),
    refetchInterval: 8000,
  });

  const runAll = useMutation({
    mutationFn: () => api.adminImportGameBillet(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-gamebillet-status"] }),
  });
  const runOne = useMutation({
    mutationFn: (c: "EUR" | "GBP" | "USD") => api.adminImportGameBillet(c),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-gamebillet-status"] }),
  });

  const currencies = status.data?.data.currencies ?? [];
  const jobs = status.data?.data.jobs ?? [];
  const offerCount = status.data?.data.offerCount ?? 0;
  const shop = status.data?.data.shop;
  const lastByCurrency: Record<string, ImportRow | undefined> = {};
  for (const j of jobs) {
    const key = j.source.split(":")[1];
    if (key && !lastByCurrency[key]) lastByCurrency[key] = j;
  }

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold">GameBillet import</h1>
          <p className="text-sm text-muted-foreground">
            Syncs the EUR / GBP / USD product feeds into offers linked to existing games.
            Matching uses Steam AppID first, then normalized title. Failed items are logged below.
          </p>
        </div>
        <button
          onClick={() => runAll.mutate()}
          disabled={runAll.isPending || !currencies.some((c) => c.configured)}
          className="inline-flex items-center gap-1 rounded-md bg-primary-gradient px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
        >
          <Play className="h-4 w-4" /> {runAll.isPending ? "Starting…" : "Run all feeds"}
        </button>
      </header>

      {runAll.isError && <ErrorState message={(runAll.error as Error).message} />}

      <div className="grid gap-4 md:grid-cols-3">
        {currencies.map((c) => {
          const j = lastByCurrency[c.currency];
          return (
            <div key={c.currency} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg font-semibold">{c.currency}</h3>
                {c.configured ? (
                  <span className="inline-flex items-center gap-1 text-xs text-success"><CheckCircle2 className="h-3 w-3" /> configured</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Circle className="h-3 w-3" /> not set</span>
                )}
              </div>
              <p className="mt-1 truncate text-xs text-muted-foreground">{c.feedUrl ?? "GAMEBILLET_FEED_" + c.currency + " env var missing"}</p>
              <div className="mt-3 space-y-1 text-xs">
                <StatusRow label="Last run" value={j ? new Date(j.startedAt).toLocaleString() : "—"} />
                <StatusRow label="Status" value={j?.status ?? "—"} tone={j?.status} />
                <StatusRow label="Products" value={j ? `${j.importedItems} imported · ${j.failedItems} failed · ${j.totalItems} total` : "—"} />
                {j?.errorMessage && (
                  <p className="mt-1 rounded bg-destructive/10 p-2 text-[11px] text-destructive">{j.errorMessage}</p>
                )}
              </div>
              <button
                onClick={() => runOne.mutate(c.currency)}
                disabled={runOne.isPending || !c.configured}
                className="mt-3 inline-flex items-center gap-1 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
              >
                <RefreshCw className="h-3 w-3" /> Run {c.currency}
              </button>
            </div>
          );
        })}
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display text-lg font-semibold">Shop</h2>
            <p className="text-xs text-muted-foreground">Automatically created on first import.</p>
          </div>
          <div className="text-right text-sm">
            <div className="font-semibold">{shop?.name ?? "Not created yet"}</div>
            <div className="text-xs text-muted-foreground">{offerCount} active offers</div>
          </div>
        </div>
      </div>

      <div>
        <h2 className="mb-3 font-display text-lg font-semibold">Recent jobs</h2>
        {status.isError && <ErrorState message={(status.error as Error).message} />}
        {jobs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No GameBillet imports have run yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-surface text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Started</th>
                  <th className="px-4 py-3">Finished</th>
                  <th className="px-4 py-3 text-right">Imported / Failed / Total</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id} className="border-t border-border/60">
                    <td className="px-4 py-2">{j.source}</td>
                    <td className="px-4 py-2">
                      <span className={
                        j.status === "completed" ? "text-success" :
                        j.status === "running" ? "text-primary" :
                        j.status === "failed" ? "text-destructive" : "text-muted-foreground"
                      }>{j.status}</span>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{new Date(j.startedAt).toLocaleString()}</td>
                    <td className="px-4 py-2 text-muted-foreground">{j.finishedAt ? new Date(j.finishedAt).toLocaleString() : "—"}</td>
                    <td className="px-4 py-2 text-right">{j.importedItems} / {j.failedItems} / {j.totalItems}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h2 className="mb-3 font-display text-lg font-semibold">Recent failed products</h2>
        {errors.isSuccess && errors.data.data.length === 0 && (
          <p className="text-sm text-muted-foreground">No errors logged.</p>
        )}
        {errors.isSuccess && errors.data.data.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-surface text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Error</th>
                </tr>
              </thead>
              <tbody>
                {errors.data.data.slice(0, 50).map((e: any) => (
                  <tr key={e.id} className="border-t border-border/60">
                    <td className="px-4 py-2 text-muted-foreground">{new Date(e.createdAt).toLocaleString()}</td>
                    <td className="px-4 py-2 text-muted-foreground">{e.rawPayload?.title ?? e.rawPayload?.name ?? "—"}</td>
                    <td className="px-4 py-2 text-destructive">{e.errorMessage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

type ImportRow = {
  id: string;
  source: string;
  status: string;
  startedAt: string;
  finishedAt?: string | null;
  totalItems: number;
  importedItems: number;
  failedItems: number;
  errorMessage?: string | null;
};

function StatusRow({ label, value, tone }: { label: string; value: string; tone?: string }) {
  const color =
    tone === "completed" ? "text-success" :
    tone === "running" ? "text-primary" :
    tone === "failed" ? "text-destructive" : "";
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={color}>{value}</span>
    </div>
  );
}
